import { Link } from '@tanstack/react-router'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { ArrowRight, Check, Clock } from 'lucide-react'
import { quietButton, secondaryButton, small } from '@/components/shell/StatusScreens'
import { latencyText, toneOf } from '@/lib/connections'
import { connectionsQuery } from '@/lib/queries'
import { cn } from '@/lib/utils'
import type { Connection } from '@/contract'
import { AreaState } from '../../../admin/shared/ui'
import { useErp } from '../../api'
import type { ErpGraph } from '../../contract'
import { CompactList, CompactRow, HowItWorks, QuietState, StateHeader } from '../../ui/calm'
import { COVERAGE_LABELS, coverageSummary, sortSystems } from '../settings-logic'
import { ConfigEyebrow, SettingsGroup } from '../ui'

/* Configurações › ERP › Integrações: um sistema por linha (de /connections, os problemas primeiro; “Abrir” leva ao
   sistema em Conexões) e o estado da base de relacionamento que alimenta o ERP (modo do grafo e o que já vem do Hub × o
   que falta sincronizar). Credenciais e testes ficam em Conexões (/conexoes). */
export function IntegrationsSection() {
    const { query } = useErp()
    const systems = useQuery(connectionsQuery)
    const graph = query.data
    const list = sortSystems(systems.data ?? [])
    const problems = list.filter(c => c.status === 'down').length
    const waiting = list.filter(c => c.status === 'waiting').length
    const cov = graph ? coverageSummary(graph.coverage) : null
    const title = !systems.data
        ? 'De onde vêm os dados do ERP.'
        : problems
            ? `${problems} ${problems === 1 ? 'sistema está' : 'sistemas estão'} sem resposta.`
            : waiting
                ? `Nenhum sistema fora do ar; ${waiting} ${waiting === 1 ? 'aguarda' : 'aguardam'} liberação.`
                : 'Todos os sistemas respondendo.'
    return (
        <>
            <StateHeader
                eyebrow={<ConfigEyebrow section="Integrações" example={graph?.dataMode === 'example'} />}
                title={title}
                sub={graph ? (graph.dataMode === 'live' ? 'A base de relacionamento chega ao vivo pelo Hub.' : 'Sem ligação com o Hub: o ERP mostra dados de exemplo.') : undefined}
                pulse={systems.data && cov ? [
                    { label: list.length === 1 ? 'sistema' : 'sistemas', value: list.length },
                    { label: 'tipos de dado sincronizados', value: `${cov.synced.length} de ${cov.synced.length + cov.missing.length}` },
                ] : undefined}
                actions={<Link to="/conexoes" className={cn(secondaryButton, small)}>Abrir Conexões<ArrowRight aria-hidden="true" /></Link>}
            />
            <SystemsList query={systems} list={list} />
            <AreaState query={query} noAccess={{ title: 'Você não vê a base de relacionamento', text: 'Peça a um administrador.' }}>
                {g => <HubCoverage graph={g} />}
            </AreaState>
            <HowItWorks>
                <p className="m-0">Cada linha é um sistema ligado ao Hub, com o estado da última verificação. “Abrir” leva ao sistema em Conexões, onde ficam credenciais, testes e o “Para liberar”.</p>
                <p className="m-0">O que a base de relacionamento ainda não traz aparece nas telas como “ainda não sincronizado”: o ERP nunca inventa dado ao vivo.</p>
            </HowItWorks>
        </>
    )
}

function SystemsList({ query, list }: { query: UseQueryResult<Connection[]>; list: Connection[] }) {
    if (query.isPending) return <div role="status" className="text-[13px] text-muted">Carregando os sistemas…</div>
    if (query.isError) return <QuietState title="Não deu para carregar os sistemas">Abra Conexões para ver o estado de cada um.</QuietState>
    return (
        <CompactList label="Sistemas" empty="Nenhum sistema ligado ainda.">
            {list.map(c => {
                const t = toneOf(c.status)
                return (
                    <CompactRow
                        key={c.slug}
                        lead={<span aria-hidden="true" className="size-2 rounded-full" style={{ background: t.dot }} />}
                        title={c.name}
                        meta={c.note ? `${c.description} · ${c.note}` : c.description}
                        right={<span className="inline-flex flex-col items-end leading-[1.25]">
                            <span className="text-[12.5px] font-medium" style={{ color: t.ink }}>{c.statusLabel || t.word}</span>
                            <span className="text-[11.5px] text-muted">{latencyText(c)}</span>
                        </span>}
                        action={<Link to="/conexoes" search={{ sistema: c.slug }} aria-label={`Abrir ${c.name} em Conexões`} className={cn(quietButton, small)}>Abrir</Link>}
                    />
                )
            })}
        </CompactList>
    )
}

function HubCoverage({ graph }: { graph: ErpGraph }) {
    const live = graph.dataMode === 'live'
    const n = (x: number) => x.toLocaleString('pt-BR')
    const entities = ([['doador', 'doadores'], ['igreja', 'igrejas'], ['evento', 'eventos'], ['consultor', 'consultores'], ['empresa', 'empresas'], ['embaixador', 'embaixadores'], ['osp', 'OSPs'], ['crianca', 'crianças']] as const)
    return (
        <SettingsGroup id="in-cobertura" title="Base de relacionamento · o que vem do Hub" aside={live ? `atualizado em ${graph.today}` : 'modo exemplo'}>
            <ul className="m-0 flex list-none flex-col p-0">
                {(Object.keys(COVERAGE_LABELS) as (keyof typeof COVERAGE_LABELS)[]).map(k => {
                    const ok = graph.coverage[k]
                    return (
                        <li key={k} className="flex min-h-10 items-center justify-between gap-3 border-t border-line text-[13.5px] first:border-t-0">
                            <span className="flex items-center gap-2">
                                {ok ? <Check size={15} aria-hidden="true" className="text-[var(--green)]" /> : <Clock size={15} aria-hidden="true" className="text-muted" />}
                                {COVERAGE_LABELS[k]}
                            </span>
                            <span className="text-[12.5px] text-muted">{ok ? 'sincronizado' : 'ainda não sincronizado'}</span>
                        </li>
                    )
                })}
            </ul>
            <p className="m-0 border-t border-line pt-2 text-[12.5px] text-muted">
                No grafo: {entities.map(([k, label]) => `${n(graph[k].length)} ${label}`).join(' · ')}.
            </p>
        </SettingsGroup>
    )
}
